/**
 * VoucherListModal - Modal for displaying vouchers for a specific month
 * 
 * Shows all vouchers with their transactions and metadata
 */

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { FileText, Calendar, ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { 
  formatAmountWithDisplayFormat, 
  determineOptimalUnit,
  type DisplayFormat,
} from '@/components/sie/utils';
import { useMemo } from 'react';

interface SIETransaction {
  account: string;
  amount: number;
  text?: string;
  quantity?: number;
  object?: string[];
}

interface SIEVoucher {
  series: string;
  number: number;
  date: string;
  text: string;
  regDate?: string;
  sign?: string;
  transactions: SIETransaction[];
}

interface VoucherListModalProps {
  isOpen: boolean;
  onClose: () => void;
  month: string; // Format: "2024-01"
  vouchers: SIEVoucher[];
  displayFormat?: DisplayFormat;
  accountNames?: Record<string, string>;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  // Handle YYYYMMDD format
  if (dateStr.length === 8 && !dateStr.includes('-')) {
    const year = dateStr.slice(0, 4);
    const month = dateStr.slice(4, 6);
    const day = dateStr.slice(6, 8);
    return `${year}-${month}-${day}`;
  }
  try {
    return new Date(dateStr).toLocaleDateString('sv-SE');
  } catch {
    return dateStr;
  }
}

function getMonthName(month: string): string {
  const monthNames: Record<string, string> = {
    '01': 'januari',
    '02': 'februari',
    '03': 'mars',
    '04': 'april',
    '05': 'maj',
    '06': 'juni',
    '07': 'juli',
    '08': 'augusti',
    '09': 'september',
    '10': 'oktober',
    '11': 'november',
    '12': 'december',
  };
  const [year, monthNum] = month.split('-');
  return `${monthNames[monthNum] || monthNum} ${year}`;
}

export function VoucherListModal({ 
  isOpen, 
  onClose, 
  month, 
  vouchers,
  displayFormat = 'auto',
  accountNames = {},
}: VoucherListModalProps) {
  // Filter vouchers for the selected month
  // SIE dates can be in YYYYMMDD format, while month is in YYYY-MM format
  const filteredVouchers = vouchers.filter(v => {
    if (!v.date) return false;
    
    // Handle YYYY-MM format (with hyphen)
    if (v.date.includes('-')) {
      return v.date.startsWith(month);
    }
    
    // Handle YYYYMMDD format (no separators) - convert month "2024-01" to "202401"
    const monthNoHyphen = month.replace('-', '');
    return v.date.startsWith(monthNoHyphen);
  });
  
  // Sort by date and then by number
  const sortedVouchers = [...filteredVouchers].sort((a, b) => {
    const dateCompare = a.date.localeCompare(b.date);
    if (dateCompare !== 0) return dateCompare;
    return a.number - b.number;
  });

  // Calculate optimal display unit for consistent formatting
  const autoUnit = useMemo(() => {
    const allAmounts = sortedVouchers.flatMap(v => 
      v.transactions.map(t => Math.abs(t.amount))
    );
    return determineOptimalUnit(allAmounts);
  }, [sortedVouchers]);

  // Helper function for formatting
  const fmt = (amount: number) => formatAmountWithDisplayFormat(amount, displayFormat, autoUnit);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Verifikationer för {getMonthName(month)}
          </DialogTitle>
          <DialogDescription>
            {sortedVouchers.length} verifikationer hittades
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[60vh] pr-4">
          {sortedVouchers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              Inga verifikationer för denna månad
            </div>
          ) : (
            <Accordion type="single" collapsible className="space-y-2">
              {sortedVouchers.map((voucher, idx) => {
                const totalDebit = voucher.transactions
                  .filter(t => t.amount > 0)
                  .reduce((sum, t) => sum + t.amount, 0);
                const totalCredit = voucher.transactions
                  .filter(t => t.amount < 0)
                  .reduce((sum, t) => sum + Math.abs(t.amount), 0);

                return (
                  <AccordionItem 
                    key={`${voucher.series}-${voucher.number}-${idx}`} 
                    value={`${voucher.series}-${voucher.number}-${idx}`}
                    className="border rounded-lg px-4"
                  >
                    <AccordionTrigger className="hover:no-underline py-3">
                      <div className="flex items-center gap-4 text-left flex-1">
                        <Badge variant="outline" className="font-mono">
                          {voucher.series}{voucher.number}
                        </Badge>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="h-3.5 w-3.5" />
                          {formatDate(voucher.date)}
                        </div>
                        <span className="flex-1 truncate text-sm">
                          {voucher.text || '-'}
                        </span>
                        <span className="text-sm font-medium">
                          {fmt(totalDebit)}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-4">
                      {/* Voucher metadata */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Serie:</span>{' '}
                          <span className="font-medium">{voucher.series}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Nummer:</span>{' '}
                          <span className="font-medium">{voucher.number}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Datum:</span>{' '}
                          <span className="font-medium">{formatDate(voucher.date)}</span>
                        </div>
                        {voucher.regDate && (
                          <div>
                            <span className="text-muted-foreground">Reg.datum:</span>{' '}
                            <span className="font-medium">{formatDate(voucher.regDate)}</span>
                          </div>
                        )}
                        {voucher.sign && (
                          <div>
                            <span className="text-muted-foreground">Signatur:</span>{' '}
                            <span className="font-medium">{voucher.sign}</span>
                          </div>
                        )}
                      </div>

                      <Separator className="my-3" />

                      {/* Transactions table */}
                      <div className="space-y-1">
                        <div className="grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground px-2 pb-1">
                          <div className="col-span-2">Konto</div>
                          <div className="col-span-3">Kontonamn</div>
                          <div className="col-span-2">Text</div>
                          <div className="col-span-2 text-right">Debet</div>
                          <div className="col-span-2 text-right">Kredit</div>
                          <div className="col-span-1"></div>
                        </div>
                        {voucher.transactions.map((trans, tIdx) => (
                          <div 
                            key={tIdx} 
                            className="grid grid-cols-12 gap-2 text-sm py-1.5 px-2 rounded hover:bg-muted/50"
                          >
                            <div className="col-span-2 font-mono text-xs">
                              {trans.account}
                            </div>
                            <div className="col-span-3 text-muted-foreground truncate text-xs">
                              {accountNames[trans.account] || '-'}
                            </div>
                            <div className="col-span-2 text-muted-foreground truncate text-xs">
                              {trans.text || '-'}
                            </div>
                            <div className="col-span-2 text-right">
                              {trans.amount > 0 ? (
                                <span className="text-foreground">{fmt(trans.amount)}</span>
                              ) : '-'}
                            </div>
                            <div className="col-span-2 text-right">
                              {trans.amount < 0 ? (
                                <span className="text-foreground">{fmt(Math.abs(trans.amount))}</span>
                              ) : '-'}
                            </div>
                            <div className="col-span-1 flex justify-end">
                              {trans.amount > 0 ? (
                                <ArrowDownRight className="h-3.5 w-3.5 text-green-600" />
                              ) : (
                                <ArrowUpRight className="h-3.5 w-3.5 text-red-600" />
                              )}
                            </div>
                          </div>
                        ))}
                        <Separator className="my-2" />
                        <div className="grid grid-cols-12 gap-2 text-sm font-medium px-2">
                          <div className="col-span-7">Summa</div>
                          <div className="col-span-2 text-right">{fmt(totalDebit)}</div>
                          <div className="col-span-2 text-right">{fmt(totalCredit)}</div>
                          <div className="col-span-1"></div>
                        </div>
                      </div>

                      {/* Object codes if present */}
                      {voucher.transactions.some(t => t.object && t.object.length > 0) && (
                        <>
                          <Separator className="my-3" />
                          <div className="text-xs text-muted-foreground">
                            <span className="font-medium">Objektkoder:</span>{' '}
                            {voucher.transactions
                              .flatMap(t => t.object || [])
                              .filter((v, i, a) => a.indexOf(v) === i)
                              .join(', ')}
                          </div>
                        </>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
